import React, { Ref, useEffect } from 'react'

import { Button, createForm, Divider, Flex, Select, Text, useToasts } from '@applyboard/crystal-ui'
import { DeleteOutlineIcon, PlusOutlineIcon } from '@applyboard/ui-icons'
import type { FileData, FileMetaData } from '@applyboard/school-applications-types-lib'

import { EmotionJSX } from '@emotion/react/types/jsx-namespace'
import { nanoid } from 'nanoid'

import { RawApplicationResponse, useUpdateApplication } from '../../../hooks'
import { ApplicationFormCard } from './ApplicationFormCard'
import { useApplicationFormContext } from './ApplicationForm'
import { AdditionalDocumentTags, DocumentTags, TimelineTabs } from '../../../utils/enums'
import { FileUploadField } from './FileUploadField'
import { hasApplicationFee } from '../../../utils'
import { convertObjToListWithSpecificLastItem, getFilesOfTypeAdditionalDocuments } from './utils'
import { StudentApplication } from '../types'
import { useGetTimelineItems } from '../../../hooks/useGetTimelineItems'
import { useGetAdditionalDocumentsTypes } from '../../../hooks/useGetAdditionalDocumentsTypes'
import { getSelectAppearance } from '../../../utils/getSelectAppearance'

type AdditionalDocument = {
  documents: Array<{
    id: string
    file: File
  }>
  documentType: string
  id: string
}

type DocumentTypeOptions = Record<AdditionalDocumentTags, { selected: boolean; label: string }>

type AdditionalDocumentsFormFields = {
  additionalDocuments: AdditionalDocument[]
  documentTypeOptions: DocumentTypeOptions
}

const { Form, Field, useFieldArray, useFieldValues, useSetFieldValues } =
  createForm<AdditionalDocumentsFormFields>()

type AdditionalDocumentsTabProps = {
  disabled?: boolean
  application: StudentApplication
  onSuccess: (response?: RawApplicationResponse) => void
  onError: (err: Error) => void
  submitApplication?: () => void
  focusRef?: Ref<HTMLButtonElement> | null
  currentIntakeAvailable?: boolean
  onIntakeUnavailable?: () => void
  onPreviousTabButton?: () => void
}

export function AdditionalDocumentsTab(props: Readonly<AdditionalDocumentsTabProps>) {
  const { timelineItems } = useGetTimelineItems(props.application)
  const documentTypesOptions = useGetAdditionalDocumentsTypes({
    files: getFilesOfTypeAdditionalDocuments(props.application?.attributes?.files as FileData),
  })
  const { isUpdatingApplication, updateApplication } = useUpdateApplication({
    id: props.application.id,
  })
  const { resetFiles, pendingFileUploadState } = useApplicationFormContext()
  const { cardNumber, lastTimeline: timelineLength } = useGetTimelineItems(props.application, TimelineTabs.OTHER_REQUIRED_DOCUMENTS)

  useEffect(() => {
    resetFiles(
      getFilesOfTypeAdditionalDocuments(props.application?.attributes?.files as FileData),
    )
  }, [props.application?.attributes?.files, resetFiles])
  const toast = useToasts()

  return (
    <Form
      id={timelineItems?.find(item => item.key === TimelineTabs.OTHER_REQUIRED_DOCUMENTS)?.formId}
      defaultValues={setDefaultAdditionalDocuments(
        props.application?.attributes?.files as FileData,
        documentTypesOptions,
      )}
      onSubmit={_data => {
        if (props.disabled) {
          props.onSuccess()
        } else if (props.onIntakeUnavailable && !props.currentIntakeAvailable) {
          props.onIntakeUnavailable()
        } else {
          updateApplication(
            {
              files: pendingFileUploadState,
            },
            {
              onSuccess: response => {
                resetFiles(
                  getFilesOfTypeAdditionalDocuments(response.data?.attributes?.files as FileData),
                )
                props.onSuccess(response)
                toast.positive('Your application has been saved successfully.')
              },
              onError: props.onError,
            },
          )
        }
      }}
    >
      <ApplicationFormCard
        cardNumber={cardNumber}
        totalCards={timelineLength}
        isLoading={isUpdatingApplication}
        onPreviousTabButton={props.onPreviousTabButton}
        icon="📄"
        title="Other Required Documents"
        submitButtonLabel={
          hasApplicationFee(props.application) ? 'Pay & Submit' : 'Save & Submit'
        }
        onSubmitApplication={() => {
          if (!props.currentIntakeAvailable && props.onIntakeUnavailable) {
            props.onIntakeUnavailable();
            return;
          }
          if (props.application) {
            props.onSuccess({ data: props.application })
          }
          updateApplication(
            {
              files: pendingFileUploadState,
            },
            {
              onSuccess: response => {
                resetFiles(
                  getFilesOfTypeAdditionalDocuments(response.data?.attributes?.files as FileData),
                )
                if (props.submitApplication) props.submitApplication() // typescript is confused...
              },
              onError: props.onError,
            },
          )
        }}
        disabled={props.disabled}
        focusRef={props.focusRef}
      >
        <Documents disabled={props.disabled} application={props.application} />
      </ApplicationFormCard>
    </Form>
  )
}

type DocumentsProps = {
  disabled?: boolean
  application: StudentApplication
}

function Documents(props: Readonly<DocumentsProps>) {
  const { addPendingDelete, getObservableFiles } = useApplicationFormContext()
  const { append, fields, remove } = useFieldArray({ name: 'additionalDocuments' })
  const { additionalDocuments, documentTypeOptions } = useFieldValues([
    'additionalDocuments',
    'documentTypeOptions',
  ])

  if (!props.disabled) {
    additionalDocuments.filter((item: AdditionalDocument) => item.documentType !== DocumentTags.TUITION_DEPOSIT_RECEIPT);
  }

  const setFieldValues = useSetFieldValues()

  return (
    <Flex direction="column" gap={6} p={2}>

      <Text variant="bodyM">Please provide any additional documents required by the program.</Text>
      <Flex direction="column" gap={4}>

        {fields.map((field, index) => {

          if (index >= additionalDocuments.length) return null

          const currentDocumentType = additionalDocuments[index].documentType
          const hasObservableFiles = !!currentDocumentType
            ? !!Object.keys(
              getObservableFiles({
                fileType: currentDocumentType,
                sectionReference: 'other',
              }),
            ).length
            : false

          return (
            <React.Fragment key={field.id}>
              <Flex>
                <Flex.Item basis={{ xs: '100%', sm: '50%' }}>
                  <Field
                    as={Select}
                    disabled={hasObservableFiles || !!props.disabled}
                    label="Document Type"
                    name={`additionalDocuments.${index}.documentType`}
                    appearance={getSelectAppearance()}
                    onChange={value => {
                      const oldDocumentType: AdditionalDocumentTags =
                        currentDocumentType as AdditionalDocumentTags
                      const newDocumentType: AdditionalDocumentTags =
                        value as AdditionalDocumentTags
                      if (documentTypeOptions[newDocumentType]) {
                        documentTypeOptions[newDocumentType].selected = true
                      }
                      if (documentTypeOptions[oldDocumentType]) {
                        documentTypeOptions[oldDocumentType].selected = false
                      }
                      setFieldValues({ documentTypeOptions: documentTypeOptions })
                    }}
                  >
                    {convertObjToListWithSpecificLastItem(documentTypeOptions, DocumentTags.ADDITIONAL_DOCUMENT).reduce(
                      (options: EmotionJSX.Element[], [documentValue, { selected, label }]) => {
                        if ((selected && documentValue !== currentDocumentType) || (!props.disabled && documentValue === DocumentTags.TUITION_DEPOSIT_RECEIPT)) return options
                        options.push(
                          <Select.Option
                            key={crypto.randomUUID()}
                            label={label}
                            value={documentValue}
                          />,
                        )
                        return options
                      },
                      [],
                    )}
                  </Field>
                </Flex.Item>
              </Flex>
              {!!currentDocumentType ? (
                <Field
                  as={FileUploadField}
                  allowedFileTypes={[
                    'pdf',
                    'png',
                    'jpeg',
                    'jpg',
                    'doc',
                    'docx',
                    'txt',
                    'xls',
                    'xlsx',
                    'mp4',
                    'avi',
                    'mov',
                    'flv',
                    'wmv',
                  ]}
                  application={props.application}
                  disabled={props.disabled}
                  fileType={DocumentTags[currentDocumentType as AdditionalDocumentTags]}
                  name={`additionalDocuments.${index}.documents`}
                  section={`other`}
                  showHistory={false}
                />
              ) : null}
              {!props.disabled && Object.keys(additionalDocuments).length > 1 ? (
                <Flex>
                  <Button
                    emphasis="highlighted"
                    intent="negative"
                    width="fill"
                    leadIcon={DeleteOutlineIcon}
                    onClick={() => {
                      const documentType = additionalDocuments[index]
                        .documentType as AdditionalDocumentTags
                      if (hasObservableFiles) {
                        Object.keys(
                          getObservableFiles({ fileType: documentType, sectionReference: 'other' }),
                        ).forEach(id => {
                          addPendingDelete(id)
                        })
                      }

                      if (!!documentType) {
                        documentTypeOptions[documentType].selected = false
                      }
                      remove(index)
                      setFieldValues({
                        documentTypeOptions: documentTypeOptions,
                      })
                    }}
                  >
                    {`Remove`}
                  </Button>
                </Flex>
              ) : null}
              <Divider />
            </React.Fragment>
          )
        })}
      </Flex>
      {!props.disabled ? (
        <Flex>
          <Button
            emphasis="highlighted"
            intent="primary"
            width="fill"
            leadIcon={PlusOutlineIcon}
            onClick={() => append(newAdditionalDocument())}
          >
            Add another supporting document
          </Button>
        </Flex>
      ) : null}
    </Flex>
  )
}

function newAdditionalDocument(): AdditionalDocument {
  return {
    documents: [],
    documentType: '',
    id: nanoid(),
  }
}

function setDefaultAdditionalDocuments(
  files: FileData | undefined,
  documentTypesOption: Record<string, { label: string }>,
): AdditionalDocumentsFormFields {
  const newDocumentTypeOptions = Object.entries(documentTypesOption).reduce<DocumentTypeOptions>(
    (newDocumentTypeOptions: DocumentTypeOptions, defaultDocumentTypeOptionEntry) => {
      const [documentType, { label }] = defaultDocumentTypeOptionEntry as [
        AdditionalDocumentTags,
        { label: string },
      ]
      newDocumentTypeOptions[documentType] = { selected: false, label: label }
      return newDocumentTypeOptions
    },
    {} as DocumentTypeOptions,
  )

  const formObject: AdditionalDocumentsFormFields = {
    additionalDocuments: [],
    documentTypeOptions: newDocumentTypeOptions,
  }

  let hasUploadedFiles = false

  Object.values(files || []).forEach((file: FileMetaData | null) => {
    if (!file || !formObject.documentTypeOptions.hasOwnProperty(file.type)) return

    const fileType: AdditionalDocumentTags = file.type as unknown as AdditionalDocumentTags
    hasUploadedFiles = true

    if (formObject.documentTypeOptions[fileType].selected) return

    formObject.documentTypeOptions[fileType].selected = true
    formObject.additionalDocuments.push({ id: nanoid(), documents: [], documentType: fileType })
  })

  if (!hasUploadedFiles) {
    formObject.additionalDocuments = [newAdditionalDocument()]
  }

  return formObject
}
